import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../Sidebar';
import Header from '../Header';
import ChatBubble from '../ChatBubble';
import ImpersonationBanner from '../../common/ImpersonationBanner/ImpersonationBanner';
import ModuleLockBoundary from '../../licensing/ModuleLockBoundary';
import PillarTabBar from '../PillarTabs/PillarTabBar';
import PageTitlePin from '../../pins/PageTitlePin';
import './AppLayout.css';

import { useLocation } from 'react-router-dom';
import { AxonPageContextProvider } from '../../../context/AxonPageContext';
import useActivityTracking from '../../../hooks/useActivityTracking';

const AppLayout = ({ children }) => {
  const location = useLocation();

  // Record page views to the platform audit trail (Management → Audit Log).
  useActivityTracking();

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(() => {
    return window.innerWidth <= 768;
  });

  // Mobile sidebar state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Default to collapsed (true) - check localStorage, but default to true if not set
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    if (saved === null) {
      // First time - set default to collapsed (true)
      localStorage.setItem('sidebar_collapsed', 'true');
      return true;
    }
    return saved === 'true';
  });

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Close mobile sidebar when switching to desktop
      if (!mobile && mobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileSidebarOpen]); // Include mobileSidebarOpen to ensure closure has current value

  useEffect(() => {
    // Apply collapsed-on-load class only on desktop.
    // On mobile this class hides sidebar headings and makes the menu appear empty.
    if (!isMobile && sidebarCollapsed) {
      document.documentElement.classList.add('sidebar-collapsed-on-load');
    } else {
      document.documentElement.classList.remove('sidebar-collapsed-on-load');
    }
  }, [sidebarCollapsed, isMobile]);

  useEffect(() => {
    let rafId = null;
    let stopRetryTimeoutId = null;
    let retryIntervalId = null;
    let syncQueued = false;

    const getSidebarIconSource = () => {
      // Preferred: exact active page nav item icon
      const activeNavIcon = document.querySelector('.sidebar .sidebar-nav .nav-link.active .nav-icon');
      if (activeNavIcon) return activeNavIcon;

      // Fallback: active section heading icon (for grouped routes/modules)
      const activeSectionIcon = document.querySelector('.sidebar .sidebar-heading.active .section-icon');
      if (activeSectionIcon) return activeSectionIcon;

      return null;
    };

    const syncHeaderIcons = () => {
      const activeSidebarIcon = getSidebarIconSource();
      if (!activeSidebarIcon) return;
      const iconSignature = activeSidebarIcon.getAttribute('data-feather') || activeSidebarIcon.className || 'sidebar-icon';

      const headerNodes = document.querySelectorAll(
        '.main-content-header h1, .main-content-header .page-title, .page-header h1, .page-title'
      );
      headerNodes.forEach((header) => {
        if (!header) return;

        // Remove hardcoded leading title icons from page-level markup.
        // We keep only the globally injected sidebar icon.
        const isIconLikeNode = (el) => {
          if (!el) return false;
          const tag = (el.tagName || '').toLowerCase();
          if (tag === 'svg' || tag === 'i') return true;
          if (el.classList?.contains('sidebar-header-icon')) return false;
          if (tag === 'span') {
            const first = el.firstElementChild;
            if (!first) return false;
            const firstTag = (first.tagName || '').toLowerCase();
            return firstTag === 'svg' || firstTag === 'i';
          }
          return false;
        };

        // Strip only leading icon-like children (before header text).
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const firstChild = header.firstElementChild;
          if (!firstChild || !isIconLikeNode(firstChild)) break;
          firstChild.remove();
        }

        const existingInjected = header.querySelector(':scope > .sidebar-header-icon');
        if (existingInjected) {
          const existingSignature = existingInjected.getAttribute('data-icon-signature');
          if (existingSignature === iconSignature) {
            // Already synced for this route/icon.
            return;
          }
          existingInjected.remove();
        }

        const iconWrapper = document.createElement('span');
        iconWrapper.className = 'sidebar-header-icon';
        iconWrapper.setAttribute('data-icon-signature', iconSignature);
        iconWrapper.setAttribute('aria-hidden', 'true');
        iconWrapper.appendChild(activeSidebarIcon.cloneNode(true));
        header.prepend(iconWrapper);
      });
    };

    const queueSync = () => {
      if (syncQueued) return;
      syncQueued = true;
      rafId = requestAnimationFrame(() => {
        syncQueued = false;
        syncHeaderIcons();
      });
    };

    // Initial sync
    syncHeaderIcons();

    // Retry on next frame for lazy/render-delayed pages
    queueSync();

    // Some pages re-render header after async data/theme/sidebar updates.
    // Keep re-syncing briefly to guarantee icon presence without long-running loops.
    retryIntervalId = window.setInterval(queueSync, 700);
    stopRetryTimeoutId = window.setTimeout(() => {
      if (retryIntervalId) {
        window.clearInterval(retryIntervalId);
        retryIntervalId = null;
      }
    }, 2500);

    // Keep synced when route content/sidebar active states update after async renders
    const observer = new MutationObserver(queueSync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (retryIntervalId) window.clearInterval(retryIntervalId);
      if (stopRetryTimeoutId) window.clearTimeout(stopRetryTimeoutId);
      observer.disconnect();
    };
  }, [location.pathname, location.search, sidebarCollapsed, isMobile]);

  const handleMenuToggle = useCallback((e) => {
    // Prevent any event propagation issues
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isMobile) {
      setMobileSidebarOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => {
        const newState = !prev;
        localStorage.setItem('sidebar_collapsed', newState.toString());
        return newState;
      });
    }
  }, [isMobile]);

  const handleMobileSidebarClose = () => {
    setMobileSidebarOpen(false);
  };

  // Determine sidebar display state
  const sidebarIsVisible = isMobile ? mobileSidebarOpen : !sidebarCollapsed;

  return (
    <AxonPageContextProvider>
      <ImpersonationBanner />
      <div className={`page-container ${isMobile ? 'mobile' : ''} ${mobileSidebarOpen ? 'mobile-sidebar-open' : ''} ${!isMobile && sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar
          isCollapsed={isMobile ? false : sidebarCollapsed}
          onToggle={handleMenuToggle}
          onMobileClose={handleMobileSidebarClose}
          isMobile={isMobile}
          isOpen={sidebarIsVisible}
        />
        {isMobile && mobileSidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={handleMobileSidebarClose}
          />
        )}
        <div className="content-wrapper">
          <Header onMenuToggle={handleMenuToggle} isMobile={isMobile} showBrand={!isMobile && sidebarCollapsed} />
          <div className="main-content-wrapper">
            <main className="main-content">
              {/* Swaps the page for the lock screen on a 403 module_not_licensed,
                  and tells nested ProtectedRoutes they are inside the chrome. */}
              {/* "New navigation": a page's related pages as tabs over it (pillarTabs.js). */}
              <PillarTabBar />
              <ModuleLockBoundary>{children}</ModuleLockBoundary>
              {/* The pin star at the end of the page title (the first <h1> here). */}
              <PageTitlePin />
            </main>
          </div>
        </div>
      </div>
      <ChatBubble />
    </AxonPageContextProvider>
  );
};

export default AppLayout;